import { useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { stressStore, crisisLevel } from "../store";
import { setAudioCrisis } from "../audio";

const WHITE = new THREE.Color("#f2f1ee");
const CRISIS = new THREE.Color("#b81a1a");
const DEEP_CRISIS = new THREE.Color("#7a0d12");

// Drives the whole room mood from the crisis level.
export function MoodDriver({ wallRef, floorRef, keyLight, fillLight }) {
  const { scene } = useThree();
  const bg = useRef(new THREE.Color("#f2f1ee"));
  const target = useRef(new THREE.Color());

  useFrame((s) => {
    const crisis = crisisLevel(stressStore.getState());
    const t = s.clock.elapsedTime;
    // pulsing crisis red — the room physically reacts
    const pulse = crisis > 0.05 ? (Math.sin(t * (2 + crisis * 5)) * 0.5 + 0.5) * crisis : 0;

    target.current.copy(WHITE).lerp(CRISIS, crisis);
    target.current.lerp(DEEP_CRISIS, pulse * 0.5);

    bg.current.lerp(target.current, 0.06);
    scene.background = bg.current;
    if (scene.fog) {
      scene.fog.color.copy(bg.current);
      scene.fog.near = 14 - crisis * 4;
      scene.fog.far = 42 - crisis * 8;
    }

    if (wallRef.current) {
      wallRef.current.material.color.copy(bg.current);
      wallRef.current.material.emissive.copy(CRISIS).multiplyScalar(pulse * 0.35);
    }
    if (floorRef.current) {
      floorRef.current.material.color.lerpColors(
        new THREE.Color("#e9e8e4"),
        new THREE.Color("#5c1113"),
        crisis
      );
    }
    if (keyLight.current) keyLight.current.intensity = 2.4 - crisis * 0.4;
    if (fillLight.current) {
      fillLight.current.color.copy(WHITE).lerp(CRISIS, crisis);
      fillLight.current.intensity = 1.1 + pulse * 1.6;
    }

    setAudioCrisis(crisis);
  });

  return null;
}

export default function RoomEnvironment() {
  const wallRef = useRef();
  const floorRef = useRef();
  const keyLight = useRef();
  const fillLight = useRef();

  return (
    <group>
      <MoodDriver wallRef={wallRef} floorRef={floorRef} keyLight={keyLight} fillLight={fillLight} />

      {/* bright museum lighting */}
      <ambientLight intensity={0.9} />
      <hemisphereLight args={["#ffffff", "#d8d8d4", 0.8]} />
      <directionalLight
        ref={keyLight}
        position={[6, 12, 6]}
        intensity={2.4}
        castShadow
        shadow-mapSize={[1024, 1024]}
        shadow-camera-far={40}
        shadow-camera-left={-12}
        shadow-camera-right={12}
        shadow-camera-top={12}
        shadow-camera-bottom={-12}
      />
      <pointLight ref={fillLight} position={[-6, 5, -4]} intensity={1.1} distance={40} />
      <pointLight position={[0, 6, 8]} intensity={0.6} color="#ffffff" />

      {/* room: large inverted box (gallery white) */}
      <mesh ref={wallRef} scale={[46, 26, 46]} position={[0, 10, 0]}>
        <boxGeometry args={[1, 1, 1]} />
        <meshStandardMaterial color="#f2f1ee" side={THREE.BackSide} roughness={0.95} metalness={0} />
      </mesh>

      {/* floor */}
      <mesh ref={floorRef} rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.02, 0]} receiveShadow>
        <circleGeometry args={[24, 64]} />
        <meshStandardMaterial color="#e9e8e4" roughness={0.4} metalness={0.15} />
      </mesh>

      {/* plinth under the pyramid */}
      <mesh position={[0, -0.08, 0]} receiveShadow castShadow>
        <cylinderGeometry args={[3.1, 3.3, 0.16, 64]} />
        <meshStandardMaterial color="#1b1b1f" roughness={0.35} metalness={0.5} />
      </mesh>
      <mesh position={[0, 0.01, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[3.0, 3.12, 64]} />
        <meshStandardMaterial color="#c9a24a" emissive="#f5c14e" emissiveIntensity={0.6} metalness={1} roughness={0.3} />
      </mesh>
    </group>
  );
}
